import { useState } from 'react';

import { UserV2 } from '../../../../../backend/src/user/user.entity';
import { TrackingEvents } from '../../../analytics/events';
import { trackCustomEvent } from '../../../analytics/Mixpanel';
import Modal from '../../../components/molecules/Modals/CustomModal';
import NewMatters from '../../../screens/Chronos/Users/components/NewMatters';
import UserMatters from '../../../screens/Chronos/Users/components/UserMatters';
import { Breadcrumb } from '../../components/breadcrumb';
import { useAddUserToMatters } from '@/api/mutations/useAddUserToMatters';
import { useRemoveUserFromMatters } from '@/api/mutations/useRemoveUserFromMatters';
import useGetMatters from '@/api/queries/useGetMatters';
import { useUserContext } from '@/Contexts/User';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { UsersTable } from '@/new/components/users-table';
import { Matter } from '@/types';

export function ManageUsers() {
  const { user } = useUserContext();
  if (!user) return null;

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserV2 | null>(null);

  const [addMatterModalOpen, setAddMatterModalOpen] = useState(false);
  const [matterSearchQuery, setMatterSearchQuery] = useState('');
  const [selectedMatters, setSelectedMatters] = useState<Set<string>>(new Set());

  const { data: mattersData, isLoading: isLoadingMatters } = useGetMatters({
    paginated: 'false',
  });
  const { mutate: addUserToMatters } = useAddUserToMatters();
  const { mutate: removeUserFromMatters } = useRemoveUserFromMatters();

  const getAvailableMatters = (): Matter[] => {
    if (!selectedUser || !mattersData?.matters) return [];
    // Get IDs of matters user already has access to and filter them out
    const userMatterIds = new Set(selectedUser.matters?.map((matter) => matter.id) || []);
    return mattersData.matters.filter((matter: Matter) => !userMatterIds.has(matter.id));
  };

  const filteredMatters = getAvailableMatters().filter(
    (matter: Matter) =>
      matter.name.toLowerCase().includes(matterSearchQuery.toLowerCase()) ||
      (matter.code && matter.code.toLowerCase().includes(matterSearchQuery.toLowerCase())) ||
      (matter.description && matter.description.toLowerCase().includes(matterSearchQuery.toLowerCase())),
  );

  // Handle matter selection
  const toggleMatterSelection = (matterId: string) => {
    const newSelectedMatters = new Set(selectedMatters);
    if (newSelectedMatters.has(matterId)) {
      newSelectedMatters.delete(matterId);
    } else {
      newSelectedMatters.add(matterId);
    }
    setSelectedMatters(newSelectedMatters);
  };

  // Handle adding selected matters to user
  const handleAddUserToMatters = () => {
    if (!selectedUser) return;

    addUserToMatters({ userId: selectedUser.id, matterIds: Array.from(selectedMatters) });

    trackCustomEvent(TrackingEvents.AddUserToMatter);

    // Close the modal and reset selection
    setAddMatterModalOpen(false);
    setSelectedMatters(new Set());
    setMatterSearchQuery('');
  };

  const handleRemoveUserFromMatter = ({ userId, matterIds }: { userId: string; matterIds: string[] }) => {
    if (!selectedUser) return;

    removeUserFromMatters({ userId, matterIds });
    // Remove the matter from the selected user
    const updatedMatters = selectedUser.matters?.filter((matter) => !matterIds.includes(matter.id));

    trackCustomEvent(TrackingEvents.RemoveUserFromMatter);

    setSelectedUser({ ...selectedUser, matters: updatedMatters });
  };

  // Handler for opening the user matters modal
  const handleOpenUserMattersModal = (user: UserV2) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  return (
    <>
      <Breadcrumb items={[{ href: '/manage-users', label: 'Manage Users', active: true }]} />
      <PageContainer>
        <PageHeader>
          <PageTitle text="Manage Users" />
        </PageHeader>

        <UsersTable organisationId={user.organisationId} onViewUserMatters={handleOpenUserMattersModal} />

        {/* Current User Matters Modal */}
        <Modal
          isOpen={modalOpen}
          handleClose={() => setModalOpen(false)}
          content={
            <UserMatters
              selectedUser={selectedUser}
              onAddToMatters={() => setAddMatterModalOpen(true)}
              removeUserFromMatters={handleRemoveUserFromMatter}
            />
          }
        />

        {/* Add To Matters Modal */}
        <Modal
          isOpen={addMatterModalOpen}
          handleClose={() => {
            setAddMatterModalOpen(false);
            setSelectedMatters(new Set());
            setMatterSearchQuery('');
          }}
          content={
            <NewMatters
              selectedUser={selectedUser}
              filteredMatters={filteredMatters}
              isLoadingMatters={isLoadingMatters}
              selectedMatters={selectedMatters}
              onToggleMatterSelection={toggleMatterSelection}
              onAddMattersToUser={handleAddUserToMatters}
              onCancel={() => {
                setAddMatterModalOpen(false);
                setSelectedMatters(new Set());
                setMatterSearchQuery('');
              }}
              matterSearchQuery={matterSearchQuery}
              onSearchQueryChange={(query) => setMatterSearchQuery(query)}
            />
          }
        />
      </PageContainer>
    </>
  );
}
